import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { publicAPI } from '../services/api';
import Icons from '../components/common/Icons';

/* ------------------------------------------------------------------ */
/*  Animated Counter Hook                                              */
/* ------------------------------------------------------------------ */
function useCountUp(end, duration = 2000, start = 0, trigger = false) {
  const [value, setValue] = useState(start);
  useEffect(() => {
    if (!trigger || !end) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setValue(Math.floor(progress * (end - start) + start));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [end, duration, start, trigger]);
  return value;
}


/* ================================================================== */
/*  MAIN COMPONENT                                                     */
/* ================================================================== */
export default function HomePage() {
  /* ---- State ---- */
  const [metrics, setMetrics] = useState({});
  const [mapPoints, setMapPoints] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const metricsRef = useRef(null);
  const [metricsVisible, setMetricsVisible] = useState(false);

  /* ---- Hero slides ---- */
  const heroSlides = [
    { img: '/images/hero-forest.jpg', tagline: 'Restoring Green Cover.', sub: 'Reviving Biodiversity.' },
    { img: '/images/coastal-mangrove.jpg', tagline: 'Mangrove Restoration.', sub: 'Shielding Shorelines.' },
    { img: '/images/mountain-valley.jpg', tagline: 'Healing Degraded Lands.', sub: 'Nurturing Native Species.' },
    { img: '/images/urban-park.jpg', tagline: 'Greener Cities.', sub: 'Healthier Communities.' },
    { img: '/images/dense-forest.jpg', tagline: 'Every Tree Planted.', sub: 'Every Life Transformed.' },
  ];

  /* ---- API Calls ---- */
  useEffect(() => {
    publicAPI.getExtendedMetrics().then(res => setMetrics(res.data.metrics)).catch(() => {
      publicAPI.getMetrics().then(res => setMetrics(res.data.metrics)).catch(() => {});
    });
    publicAPI.getPlantationMap().then(res => setMapPoints(res.data.points)).catch(() => {});
  }, []);

  /* ---- Hero auto-advance ---- */
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  /* ---- Metrics visibility for counter animation ---- */
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setMetricsVisible(true); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    if (metricsRef.current) observer.observe(metricsRef.current);
    return () => observer.disconnect();
  }, []);

  /* ---- Scroll-based fade-in for .fade-section ---- */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('.fade-section').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  /* ---- Animated counters ---- */
  const treesCount = useCountUp(metrics.total_trees_planted || 0, 2000, 0, metricsVisible);
  const orgsCount = useCountUp(metrics.total_organisations || 0, 2000, 0, metricsVisible);
  const indivCount = useCountUp(metrics.total_individuals || 0, 2000, 0, metricsVisible);
  const carbonCount = useCountUp(Math.round((metrics.carbon_sequestration_kg || 0) / 1000), 2000, 0, metricsVisible);
  const agencyCount = useCountUp(metrics.total_agencies || 0, 2000, 0, metricsVisible);
  const sitesCount = useCountUp(metrics.plantation_sites || 0, 2000, 0, metricsVisible);
  const statesCount = useCountUp(metrics.states_covered || 0, 2000, 0, metricsVisible);

  /* ---- Smooth scroll helper ---- */
  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, []);

  /* ---- Testimonials data ---- */
  const testimonials = [
    { name: 'Environmentalist', role: 'Green Advocate', text: 'Haritanjali is doing exceptional work in restoring India\'s green cover. Their transparent approach and technology-driven monitoring sets a new standard for plantation initiatives.' },
    { name: 'CSR Partner', role: 'Corporate Sustainability', text: 'We partnered with Haritanjali for our CSR plantation goals. The real-time tracking and geo-tagged verification gave us complete confidence in the impact we are making.' },
    { name: 'Government Official', role: 'Forest Department', text: 'The Ek Ped Maa Ke Naam campaign channelled through Haritanjali has shown how technology can amplify government initiatives and engage citizens at scale.' },
    { name: 'Climate Activist', role: 'Environmental Action', text: 'What sets Haritanjali apart is their focus on locally appropriate species and community involvement. This is how plantation drives should be run across India.' },
    { name: 'Agency Partner', role: 'Implementation Lead', text: 'As an implementing agency, working with Haritanjali has been seamless. The platform\'s milestone-based disbursement ensures accountability and timely fund flow.' },
    { name: 'Researcher', role: 'Botany & Ecology', text: 'Haritanjali\'s emphasis on native species and biodiversity restoration is commendable. Their scientific approach to plantation planning is exactly what India needs.' },
  ];


  /* ================================================================ */
  /*  RENDER                                                           */
  /* ================================================================ */
  return (
    <>
      {/* ============== GLOBAL INLINE STYLES / KEYFRAMES ============== */}
      <style>{`
        .fade-section {
          opacity: 0; transform: translateY(40px);
          transition: opacity 0.7s ease-out, transform 0.7s ease-out;
        }
        .fade-visible { opacity: 1; transform: translateY(0); }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes bounceDown {
          0%,100%{ transform: translateY(0); }
          50%{ transform: translateY(12px); }
        }
        @keyframes slideFadeIn {
          from { opacity: 0; transform: translateY(30px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .stakeholder-hover:hover {
          transform: translateY(-8px);
          box-shadow: 0 16px 40px rgba(46,125,50,0.15) !important;
          border-color: #2e7d32 !important;
        }
        .step-circle:hover {
          transform: scale(1.12);
          box-shadow: 0 0 0 12px rgba(46,125,50,0.12);
        }
        /* Hero carousel */
        .hero-slide {
          position: absolute; inset: 0;
          background-size: cover; background-position: center;
          transition: opacity 1s ease-in-out;
        }
        /* Testimonials scroll */
        @keyframes scrollTestimonials {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .testimonials-track {
          display: flex;
          animation: scrollTestimonials 40s linear infinite;
          width: max-content;
        }
        .testimonials-track:hover {
          animation-play-state: paused;
        }
        .testimonials-track > div {
          margin-right: 24px;
        }
        /* Process timeline responsive */
        @media (max-width: 900px) {
          .process-timeline-steps {
            flex-direction: column !important;
            align-items: center !important;
          }
          .process-timeline-line {
            display: none !important;
          }
        }
      `}</style>


      {/* ==================== 1. HERO CAROUSEL ==================== */}
      <section
        className="hero"
        style={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 20px',
          overflow: 'hidden',
        }}
      >
        {/* Background slides */}
        {heroSlides.map((slide, i) => (
          <div
            key={i}
            className="hero-slide"
            style={{
              backgroundImage: `url(${slide.img})`,
              opacity: i === currentSlide ? 1 : 0,
              zIndex: 0,
            }}
          />
        ))}

        {/* Dark overlay */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(27,94,32,0.65) 100%)',
          zIndex: 1,
        }} />

        <div style={{
          position: 'relative', zIndex: 2, textAlign: 'center',
          maxWidth: 860, color: '#fff',
        }}>
          <p style={{
            fontSize: 14, letterSpacing: 4, textTransform: 'uppercase',
            marginBottom: 20, opacity: 0.85, fontWeight: 500,
            animation: 'slideFadeIn 1s ease-out forwards',
          }}>
            India's Platform for Climate Action
          </p>
          <h1
            key={currentSlide}
            style={{
              fontSize: 'clamp(34px, 5.5vw, 64px)',
              fontWeight: 800, lineHeight: 1.12, marginBottom: 24,
              textShadow: '0 2px 30px rgba(0,0,0,0.3)',
              animation: 'slideFadeIn 0.8s ease-out forwards',
            }}
          >
            {heroSlides[currentSlide].tagline}<br />{heroSlides[currentSlide].sub}
          </h1>
          <p style={{
            fontSize: 'clamp(16px, 2vw, 21px)',
            opacity: 0.92, maxWidth: 640, margin: '0 auto 40px',
            lineHeight: 1.7,
          }}>
            Combating climate change through locally appropriate plantation activities.
            One tree, one community, one planet at a time.
          </p>

          <div style={{
            display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap',
          }}>
            <Link to="/register" className="btn" style={{
              background: '#ff9800', color: '#fff', border: 'none',
              padding: '16px 40px', fontSize: 17, fontWeight: 700,
              borderRadius: 50, boxShadow: '0 4px 20px rgba(255,152,0,0.4)',
              transition: 'all 0.3s',
            }}>
              Plant a Tree
            </Link>
            <button onClick={() => scrollTo('about')} className="btn" style={{
              background: 'rgba(255,255,255,0.12)', color: '#fff',
              border: '2px solid rgba(255,255,255,0.5)',
              padding: '16px 40px', fontSize: 17, fontWeight: 600,
              borderRadius: 50, cursor: 'pointer',
              backdropFilter: 'blur(4px)', transition: 'all 0.3s',
            }}>
              Partner With Us
            </button>
          </div>
        </div>

        {/* Dot indicators */}
        <div style={{
          position: 'absolute', bottom: 60, left: '50%',
          transform: 'translateX(-50%)', zIndex: 2,
          display: 'flex', gap: 10,
        }}>
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              style={{
                width: i === currentSlide ? 28 : 10,
                height: 10, borderRadius: 5,
                background: i === currentSlide ? '#ff9800' : 'rgba(255,255,255,0.5)',
                border: 'none', cursor: 'pointer',
                transition: 'all 0.3s',
              }}
            />
          ))}
        </div>

        {/* Scroll indicator */}
        <div
          onClick={() => scrollTo('impact')}
          style={{
            position: 'absolute', bottom: 20, left: '50%',
            transform: 'translateX(-50%)', zIndex: 2,
            cursor: 'pointer', color: 'white', textAlign: 'center',
            animation: 'bounceDown 2s infinite',
          }}
        >
          <Icons.ArrowDown />
        </div>
      </section>


      {/* ==================== 2. IMPACT METRICS (circle-icon layout) ==================== */}
      <section
        ref={metricsRef}
        className="metrics-section"
        id="impact"
        style={{
          padding: '80px 20px',
          background: '#ffffff',
          textAlign: 'center',
        }}
      >
        <div className="fade-section">
          <p style={{ color: '#2e7d32', fontWeight: 600, fontSize: 14, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>Our Collective Impact</p>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', color: '#1b5e20', marginBottom: 12 }}>
            Together, We Are Making a Difference
          </h2>
          <p style={{ color: '#555', maxWidth: 600, margin: '0 auto 50px', fontSize: 17 }}>
            Real-time data from plantation drives across India
          </p>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: 'clamp(16px, 3vw, 40px)',
          maxWidth: 1200,
          margin: '0 auto',
          overflowX: 'auto',
        }}>
          {[
            { icon: <Icons.Tree />, value: treesCount, suffix: '+', label: 'Trees Planted' },
            { icon: <Icons.Building />, value: orgsCount, suffix: '+', label: 'CSR Partners' },
            { icon: <Icons.User />, value: indivCount, suffix: '+', label: 'Individuals' },
            { icon: <Icons.Gear />, value: agencyCount, suffix: '+', label: 'Agencies' },
            { icon: <Icons.Target />, value: sitesCount, suffix: '', label: 'Plantation Sites' },
            { icon: <Icons.Globe />, value: statesCount, suffix: '', label: 'States Covered' },
            { icon: <Icons.Leaf />, value: carbonCount, suffix: ' MT', label: 'Carbon Sequestered' },
          ].map((m, i) => (
            <div
              key={i}
              className="fade-section"
              style={{
                textAlign: 'center',
                flex: '0 0 auto',
                minWidth: 120,
                transitionDelay: `${i * 0.06}s`,
              }}
            >
              <div style={{
                width: 72, height: 72, borderRadius: '50%',
                border: '2px solid #c5a236',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px',
                color: '#7a6c2a',
              }}>
                {m.icon}
              </div>
              <div style={{
                fontSize: 'clamp(24px, 2.5vw, 34px)', fontWeight: 800,
                color: '#1b5e20', lineHeight: 1.1, marginBottom: 6,
              }}>
                {m.value.toLocaleString()}{m.suffix}
              </div>
              <div style={{ color: '#888', fontSize: 14, fontWeight: 400 }}>
                {m.label}
              </div>
            </div>
          ))}
        </div>
      </section>


      {/* ==================== 3. ABOUT ==================== */}
      <section
        id="about"
        className="about-section"
        style={{ padding: '100px 20px', background: '#fff' }}
      >
        <div className="fade-section" style={{
          maxWidth: 1200, margin: '0 auto',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: 60, alignItems: 'center',
        }}>
          <div style={{ position: 'relative' }}>
            <img
              src="/images/hands-seedling.jpg"
              alt="Hands nurturing a plant"
              style={{
                width: '100%', borderRadius: 20,
                boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                objectFit: 'cover', maxHeight: 480,
              }}
            />
            <div style={{
              position: 'absolute', bottom: -20, right: -20,
              background: '#ff9800', color: 'white', borderRadius: 16,
              padding: '20px 28px', fontWeight: 700, fontSize: 18,
              boxShadow: '0 8px 24px rgba(255,152,0,0.3)',
            }}>
              Since 2024
            </div>
          </div>
          <div>
            <p style={{ color: '#2e7d32', fontWeight: 600, fontSize: 14, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>Who We Are</p>
            <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 40px)', color: '#1b5e20', marginBottom: 24, lineHeight: 1.2 }}>
              A Digital Platform for<br />Climate Action
            </h2>
            <p style={{ fontSize: 17, color: '#555', lineHeight: 1.8, marginBottom: 20 }}>
              Haritanjali (हरितांजलि) is India's comprehensive digital platform for combating climate change through
              locally appropriate plantation activities. We connect CSR entities, implementing agencies,
              and individuals to create a transparent, accountable ecosystem for restoring green cover
              and biodiversity.
            </p>
            <p style={{ fontSize: 17, color: '#555', lineHeight: 1.8, marginBottom: 30 }}>
              Through technology-driven monitoring, geo-tagged verification, and real-time progress
              tracking, we ensure that every planted tree is accounted for and nurtured to maturity.
            </p>
            <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
              {[
                { num: '100%', text: 'Transparent' },
                { num: 'Geo-tagged', text: 'Verification' },
                { num: 'Real-time', text: 'Monitoring' },
              ].map((item, i) => (
                <div key={i} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#2e7d32' }}>{item.num}</div>
                  <div style={{ fontSize: 13, color: '#777', fontWeight: 500, marginTop: 2 }}>{item.text}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* Section 4 (What Trees Do For You) moved to /tree-benefits page */}


      {/* ==================== 5. SUPPORTING GOVERNMENT INITIATIVE ==================== */}
      <section
        className="govt-initiative"
        style={{
          position: 'relative',
          padding: '100px 20px',
          background: '#fff',
          overflow: 'hidden',
        }}
      >
        <div className="fade-section" style={{
          maxWidth: 1200, margin: '0 auto',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: 60, alignItems: 'center',
        }}>
          <div>
            <div style={{
              display: 'inline-block',
              background: 'linear-gradient(135deg, #ff9800, #ff5722)',
              color: 'white', borderRadius: 30, padding: '6px 20px',
              fontSize: 13, fontWeight: 600, letterSpacing: 1,
              textTransform: 'uppercase', marginBottom: 20,
            }}>
              Supporting Government Initiative
            </div>
            <h2 style={{ fontSize: 'clamp(28px, 3.5vw, 42px)', color: '#1b5e20', marginBottom: 24, lineHeight: 1.15 }}>
              Ek Ped Maa Ke Naam
            </h2>
            <p style={{ fontSize: 17, color: '#555', lineHeight: 1.8, marginBottom: 30 }}>
              Launched by the Hon'ble Prime Minister Shri Narendra Modi on World Environment Day 2024,
              this landmark campaign combines environmental responsibility with a heartfelt tribute to
              mothers across the nation. Citizens plant a tree in honour of their mothers, creating
              living memorials that contribute to India's green cover.
            </p>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 30,
            }}>
              <div style={{
                background: 'linear-gradient(135deg, #e8f5e9, #f1f8e9)',
                borderRadius: 16, padding: '24px 20px', textAlign: 'center',
              }}>
                <div style={{ fontSize: 36, fontWeight: 800, color: '#2e7d32' }}>80 Crore</div>
                <div style={{ fontSize: 14, color: '#666', marginTop: 4 }}>Seedlings Planted</div>
              </div>
              <div style={{
                background: 'linear-gradient(135deg, #fff3e0, #fff8e1)',
                borderRadius: 16, padding: '24px 20px', textAlign: 'center',
              }}>
                <div style={{ fontSize: 36, fontWeight: 800, color: '#e65100' }}>5 Lakh</div>
                <div style={{ fontSize: 14, color: '#666', marginTop: 4 }}>Saplings in One Hour</div>
              </div>
            </div>
            <p style={{ fontSize: 14, color: '#888', fontStyle: 'italic' }}>
              A Guinness World Record for planting 5 lakh saplings within a single hour.
            </p>
          </div>
          <div style={{ position: 'relative' }}>
            <img
              src="/images/crops-field.jpg"
              alt="Seedlings growing in soil"
              style={{
                width: '100%', borderRadius: 20,
                boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
                objectFit: 'cover', maxHeight: 500,
              }}
            />
            <div style={{
              position: 'absolute', top: 20, left: 20,
              background: 'rgba(255,255,255,0.95)',
              borderRadius: 12, padding: '12px 20px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            }}>
              <div style={{ fontSize: 13, color: '#888', fontWeight: 500 }}>World Environment Day</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#2e7d32' }}>June 5, 2024</div>
            </div>
          </div>
        </div>
      </section>


      {/* ==================== 6. OUR ECOSYSTEM (Volunteers replaces Admin) ==================== */}
      <section
        className="stakeholder-section"
        style={{
          padding: '100px 20px',
          background: 'linear-gradient(180deg, #f5f7f5 0%, #e8f5e9 100%)',
        }}
      >
        <div className="fade-section" style={{ textAlign: 'center', marginBottom: 60 }}>
          <p style={{ color: '#2e7d32', fontWeight: 600, fontSize: 14, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>Our Ecosystem</p>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', color: '#1b5e20', marginBottom: 16 }}>
            Who Can Join Haritanjali?
          </h2>
          <p style={{ color: '#666', maxWidth: 600, margin: '0 auto', fontSize: 17 }}>
            A collaborative ecosystem connecting funders, planters, and verifiers
          </p>
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 24, maxWidth: 1200, margin: '0 auto',
        }}>
          {[
            {
              icon: <Icons.Briefcase />, title: 'CSR Entities',
              desc: 'Post plantation projects, fund initiatives, track progress in real-time, and generate verified impact reports for CSR compliance.',
              color: '#1565c0',
            },
            {
              icon: <Icons.HeartHand />, title: 'Individuals',
              desc: 'Sponsor trees, track their growth with geo-tagged photos, volunteer at plantation drives, and dedicate trees to loved ones.',
              color: '#e65100',
            },
            {
              icon: <Icons.ClipboardCheck />, title: 'Implementing Agencies',
              desc: 'Execute plantation projects, submit geo-tagged progress reports, achieve milestones, and receive verified fund disbursements.',
              color: '#2e7d32',
            },
            {
              icon: <Icons.HandsRaised />, title: 'Volunteers',
              desc: 'Join plantation drives in your community, contribute your time and effort, and become a champion for local green cover restoration.',
              color: '#7b1fa2',
            },
          ].map((s, i) => (
            <div
              key={i}
              className="stakeholder-card stakeholder-hover fade-section"
              style={{
                background: '#fff',
                borderRadius: 16, padding: '36px 28px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                border: '2px solid transparent',
                transition: 'all 0.35s ease',
                transitionDelay: `${i * 0.1}s`,
                textAlign: 'center',
              }}
            >
              <div style={{
                width: 72, height: 72, borderRadius: '50%',
                background: `${s.color}14`, color: s.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px',
              }}>
                {s.icon}
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700, color: '#1b5e20', marginBottom: 12 }}>{s.title}</h3>
              <p style={{ fontSize: 15, color: '#666', lineHeight: 1.7 }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>


      {/* ==================== 7. HOW IT WORKS (Timeline Design) ==================== */}
      <section style={{ padding: '100px 20px', background: '#fff' }}>
        <div className="fade-section" style={{ textAlign: 'center', marginBottom: 60 }}>
          <p style={{ color: '#2e7d32', fontWeight: 600, fontSize: 14, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>Simple Process</p>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', color: '#1b5e20', marginBottom: 16 }}>
            How It Works
          </h2>
          <p style={{ color: '#666', maxWidth: 550, margin: '0 auto', fontSize: 17 }}>
            Get started in four simple steps and begin your journey towards a greener planet
          </p>
        </div>

        <div className="fade-section" style={{ position: 'relative', maxWidth: 1000, margin: '0 auto' }}>
          {/* Dashed connector line */}
          <div className="process-timeline-line" style={{
            position: 'absolute', top: 40, left: '14%', right: '14%',
            height: 0, borderTop: '2.5px dashed #a5d6a7', zIndex: 0,
          }} />

          {/* Steps */}
          <div className="process-timeline-steps" style={{
            display: 'flex', justifyContent: 'space-between',
            position: 'relative', zIndex: 1,
          }}>
            {[
              { num: '01', title: 'Register', desc: 'Create your account as a CSR entity, individual, or implementing agency.' },
              { num: '02', title: 'Get Verified', desc: 'Our team verifies your identity and credentials for trust and accountability.' },
              { num: '03', title: 'Choose Action', desc: 'Fund a plantation, sponsor trees, apply for projects, or volunteer at drives.' },
              { num: '04', title: 'Track Impact', desc: 'Monitor progress with geo-tagged photos, growth reports, and real-time dashboards.' },
            ].map((step, i) => (
              <div key={i} style={{
                textAlign: 'center', flex: '1 1 0', maxWidth: 220, padding: '0 10px',
              }}>
                <div style={{
                  width: 80, height: 80, borderRadius: '50%',
                  background: '#fff', border: '3px solid #2e7d32',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 20px',
                  fontSize: 26, fontWeight: 800, color: '#2e7d32',
                  boxShadow: '0 4px 16px rgba(46,125,50,0.15)',
                  transition: 'all 0.3s ease',
                  cursor: 'default',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#2e7d32'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.transform = 'scale(1.1)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#2e7d32'; e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  {step.num}
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1b5e20', marginBottom: 8 }}>{step.title}</h3>
                <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ==================== 8. PLANTATION MAP ==================== */}
      <section className="map-section" style={{ padding: '100px 20px', background: '#f5f7f5' }}>
        <div className="fade-section" style={{ textAlign: 'center', marginBottom: 50 }}>
          <p style={{ color: '#2e7d32', fontWeight: 600, fontSize: 14, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>
            Across the Nation
          </p>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', color: '#1b5e20', marginBottom: 16 }}>
            Plantation Map
          </h2>
          <p style={{ color: '#666', maxWidth: 550, margin: '0 auto', fontSize: 17 }}>
            Explore plantation sites and track green cover restoration across India
          </p>
        </div>
        <div className="map-container fade-section" style={{
          height: 520, borderRadius: 20, overflow: 'hidden',
          maxWidth: 1200, margin: '0 auto',
          boxShadow: '0 8px 40px rgba(0,0,0,0.1)',
          border: '3px solid #e0e0e0',
        }}>
          <MapContainer
            center={[20.5937, 78.9629]}
            zoom={5}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={false}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            {mapPoints.map(point => (
              <Marker key={point.id} position={[point.latitude, point.longitude]}>
                <Popup>
                  <strong>{point.title}</strong><br />
                  {point.total_plants} plants - {point.city}, {point.state}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </section>


      {/* ==================== 9. BLESSINGS (Testimonials) ==================== */}
      <section style={{
        padding: '80px 0',
        background: 'linear-gradient(135deg, #f1f8e9 0%, #e8f5e9 100%)',
        overflow: 'hidden',
      }}>
        <div className="fade-section" style={{ textAlign: 'center', marginBottom: 50, padding: '0 20px' }}>
          <p style={{ color: '#2e7d32', fontWeight: 600, fontSize: 14, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>
            Voices of Support
          </p>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 42px)', color: '#1b5e20', marginBottom: 16 }}>
            Blessings
          </h2>
          <p style={{ color: '#666', maxWidth: 550, margin: '0 auto', fontSize: 17 }}>
            Words of encouragement from environmentalists, leaders, and partners
          </p>
        </div>

        <div style={{ overflow: 'hidden', padding: '0 0 10px' }}>
          <div className="testimonials-track">
            {[...testimonials, ...testimonials].map((t, i) => (
              <div
                key={i}
                style={{
                  flex: '0 0 380px', background: '#fff',
                  borderRadius: 16, padding: '32px 28px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                  border: '1px solid rgba(46,125,50,0.08)',
                  position: 'relative',
                }}
              >
                <div style={{
                  position: 'absolute', top: 16, right: 20,
                  fontSize: 48, color: 'rgba(46,125,50,0.08)',
                  fontFamily: 'Georgia, serif', lineHeight: 1,
                }}>
                  &ldquo;
                </div>
                <p style={{
                  color: '#555', fontSize: 15, lineHeight: 1.7,
                  marginBottom: 20, fontStyle: 'italic',
                  position: 'relative', zIndex: 1,
                  minHeight: 90,
                }}>
                  {t.text}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 46, height: 46, borderRadius: '50%',
                    background: '#e8f5e9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#66bb6a',
                    flexShrink: 0, overflow: 'hidden',
                  }}>
                    <Icons.AvatarPlaceholder />
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#1b5e20' }}>{t.name}</div>
                    <div style={{ fontSize: 13, color: '#888' }}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ==================== 10. CALL TO ACTION ==================== */}
      <section
        className="cta-section"
        style={{
          position: 'relative',
          padding: '120px 20px',
          backgroundImage: 'url(/images/canopy-lookup.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          textAlign: 'center',
        }}
      >
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(135deg, rgba(27,94,32,0.88) 0%, rgba(0,0,0,0.7) 100%)',
        }} />
        <div className="fade-section" style={{ position: 'relative', zIndex: 2, maxWidth: 750, margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(30px, 5vw, 52px)',
            color: '#fff', fontWeight: 800,
            lineHeight: 1.15, marginBottom: 24,
          }}>
            Join the Green Revolution.<br />Every Tree Counts.
          </h2>
          <p style={{
            fontSize: 19, color: 'rgba(255,255,255,0.85)',
            maxWidth: 550, margin: '0 auto 40px', lineHeight: 1.7,
          }}>
            Whether you are a corporation fulfilling CSR goals, an individual who cares, or
            an agency ready to execute, your role matters.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/register" className="btn" style={{
              background: '#ff9800', color: '#fff', border: 'none',
              padding: '18px 48px', fontSize: 18, fontWeight: 700,
              borderRadius: 50, boxShadow: '0 6px 24px rgba(255,152,0,0.4)',
              transition: 'all 0.3s',
            }}>
              Register Now
            </Link>
            <button onClick={() => scrollTo('about')} className="btn" style={{
              background: 'rgba(255,255,255,0.1)', color: '#fff',
              border: '2px solid rgba(255,255,255,0.5)',
              padding: '18px 48px', fontSize: 18, fontWeight: 600,
              borderRadius: 50, cursor: 'pointer',
              backdropFilter: 'blur(4px)', transition: 'all 0.3s',
            }}>
              Learn More
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
